import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { BASE_URL } from "../config";
import DeviceKioskHeader from "./DeviceKioskHeader";
import { loadManageContext, saveManageContext } from "./manageContext";
import { saveManageSession } from "./manageSession";

const DEVICE_SIGNIN_EMAIL_KEY = "deviceSigninEmail";

export default function DeviceSignin() {
  usePageMeta("Assign Location");
  const navigate = useNavigate();
  const location = useLocation();
  const [now, setNow] = useState(new Date());
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const manageContext = useMemo(() => loadManageContext(location), [location]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const remembered =
      typeof window !== "undefined"
        ? localStorage.getItem(DEVICE_SIGNIN_EMAIL_KEY)
        : null;
    if (remembered) {
      setEmail(String(remembered));
    }
  }, []);

  const handleContinue = () => {
    const nextEmail = String(email || "").trim().toLowerCase();
    if (!nextEmail) {
      setError("Email is required.");
      return;
    }
    setError("");
    setEmail(nextEmail);
    if (typeof window !== "undefined") {
      localStorage.setItem(DEVICE_SIGNIN_EMAIL_KEY, nextEmail);
    }
    saveManageContext({
      ...manageContext,
      email: nextEmail,
      clientName: manageContext?.clientName || "Client",
    });
    setStep("password");
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const normalizedPassword = String(password || "");
    if (!normalizedEmail || !normalizedPassword) {
      setError("Email and password are required.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${BASE_URL}/auth/manage-signin-device`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: normalizedEmail,
          password: normalizedPassword,
          clientDeviceId: manageContext?.clientDeviceId || null,
          browserDeviceGuid: manageContext?.browserDeviceGuid || null,
        }),
      });
      const rawBody = await response.text();
      let payload = {};
      if (rawBody) {
        try {
          payload = JSON.parse(rawBody);
        } catch {
          payload = {};
        }
      }
      if (!response.ok) {
        let msg =
          typeof payload?.message === "string" && payload.message.trim()
            ? payload.message.trim()
            : "";
        if (
          /not found/i.test(msg) &&
          /manage-signin-device|\/auth\//i.test(msg)
        ) {
          msg =
            "Sign-in service was not found on this server. Confirm REACT_APP_BACKEND_URL points at the API host (for example http://localhost:5000), not the admin site URL.";
        }
        if (!msg && response.status === 404) {
          msg =
            "Sign-in service was not found. Confirm REACT_APP_BACKEND_URL points at the API host that exposes /auth/manage-signin-device.";
        }
        if (!msg) {
          msg = `Unable to sign in (${response.status}).`;
        }
        throw new Error(msg);
      }

      saveManageSession({
        token: payload?.token,
        user: {
          accountId: payload?.account_id,
          email: payload?.email,
          clientId: payload?.client_id || null,
          capabilities: payload?.capabilities || { canManageDevice: true },
          roles: payload?.roles || [],
          permissions: payload?.permissions || [],
        },
      });
      saveManageContext({
        ...manageContext,
        email: normalizedEmail,
        accountId: payload?.account_id || manageContext?.accountId || null,
        clientName: manageContext?.clientName || "Client",
      });

      navigate("/device/assign", {
        state: {
          email: normalizedEmail,
          clients: Array.isArray(payload?.clients) ? payload.clients : [],
        },
      });
    } catch (err) {
      setError(err?.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <DeviceKioskHeader now={now} />
      <main className="flex-1 flex flex-col px-4">
        <h1 className="mt-6 text-center text-2xl text-gray-700">Assign Location</h1>

        <div className="mx-auto mt-14 w-full max-w-sm">
          <h2 className="mb-2 text-center text-3xl text-gray-700">Sign In</h2>

          {step === "email" ? (
            <>
              <label className="block text-sm text-gray-500 mb-1">Email*</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-b border-gray-300 bg-transparent py-2 text-sm outline-none focus:border-gray-500"
              />
              <button
                type="button"
                onClick={handleContinue}
                className="mt-6 w-full bg-[#DC1F00] text-white py-2 text-sm font-medium uppercase hover:bg-[#b91900] transition-colors"
              >
                Continue
              </button>
            </>
          ) : (
            <form onSubmit={handleSignIn}>
              <div className="mb-8 text-center text-sm text-gray-700">
                {email}{" "}
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setPassword("");
                    setError("");
                  }}
                  className="text-[#DC1F00]"
                >
                  Change
                </button>
              </div>

              <label className="block text-sm text-gray-500 mb-1">Password*</label>
              <div className="flex items-center border-b border-gray-300">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent py-2 text-sm outline-none focus:border-gray-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-xs text-gray-500"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="mt-6 w-full bg-[#DC1F00] text-white py-2 text-sm font-medium uppercase hover:bg-[#b91900] transition-colors disabled:opacity-60"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/forgot-password?email=${encodeURIComponent(email)}`)}
                className="mt-2 w-full text-center text-xs text-gray-700 hover:text-black"
              >
                Forgot Password
              </button>
            </form>
          )}

          {error ? <div className="mt-3 text-center text-sm text-red-600">{error}</div> : null}
        </div>
      </main>
      <div className="px-4 pb-4">
        <button
          type="button"
          onClick={() => navigate("/device")}
          className="w-full bg-red-50 text-red-400 py-2 text-sm font-medium uppercase hover:bg-red-100 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
